import { Input, OutlinedInput, OutlinedInputProps, Skeleton, SxProps, Theme } from '@mui/material';
import React from 'react';
import characters from 'src/constants/characters';
import { ClickElementType } from 'src/lib/interactionTypes';
import { Typography } from '../Typography';
import { Field, FieldProps } from './Field';
import withFormConsumer from './withFormConsumer';

type TextFieldVariant = 'outlined' | 'text';
type TextFieldEditor = 'plain' | 'markdown';

const MarkdownInput = React.lazy(() =>
    import('./MarkdownInput').then((module) => ({ default: module.MarkdownInput })),
);

export interface TextFieldProps
    extends
        Pick<
            FieldProps,
            | 'editable'
            | 'error'
            | 'helperText'
            | 'helperTextStyle'
            | 'label'
            | 'labelAdornment'
            | 'inlineLabelAdornment'
            | 'tooltip'
            | 'wide'
            | 'required'
            | 'errorId'
        >,
        Pick<
            OutlinedInputProps,
            | 'autoComplete'
            | 'autoFocus'
            | 'className'
            | 'defaultValue'
            | 'disabled'
            | 'id'
            | 'multiline'
            | 'name'
            | 'onMouseDown'
            | 'placeholder'
            | 'rows'
            | 'maxRows'
            | 'style'
            | 'type'
            | 'onBlur'
            | 'onChange'
            | 'onClick'
            | 'onKeyDown'
            | 'value'
            | 'size'
            | 'startAdornment'
            | 'endAdornment'
            | 'inputProps'
            | 'inputRef'
        > {
    trimWhiteSpace?: boolean;
    variant?: TextFieldVariant;
    editor?: TextFieldEditor;
    emptyNode?: React.ReactNode;
    inputSx?: SxProps<Theme>;
    autoCapitalize?: string;
    autoCorrect?: string;
    spellCheck?: boolean;
}

export const Text = ({
    editable = true,
    error,
    helperText,
    helperTextStyle,
    label,
    labelAdornment,
    inlineLabelAdornment,
    tooltip,
    value,
    onChange,
    trimWhiteSpace = false,
    wide = false,
    required = false,
    errorId,
    variant = 'outlined',
    editor = 'plain',
    emptyNode = characters.emdash,
    inputSx,
    autoCapitalize,
    autoCorrect,
    spellCheck,
    // @ts-expect-error - disableUnderline is used by some text-variant callers; drop it from the outlined path
    disableUnderline,
    inputProps: inputElementProps,
    // @ts-expect-error - InputProps is not in TextFieldProps but may be passed by callers; destructure to prevent DOM leak
    InputProps: _InputProps,
    ...inputProps
}: TextFieldProps) => {
    const isMarkdownEditor = editor === 'markdown';
    const stringValue = String(value ?? '');
    const baseOutlinedSx: SxProps<Theme> = {
        backgroundColor: (theme) => theme.resqPalette.surface.default,
        color: (theme) => theme.resqPalette.text.default,
        '& .MuiOutlinedInput-input, & .MuiInputBase-input': {
            color: (theme) => theme.resqPalette.text.default,
            '&::placeholder': {
                color: (theme) => theme.resqPalette.text.placeHolder,
                WebkitTextFillColor: (theme) => theme.resqPalette.text.placeHolder,
                opacity: 1,
            },
        },
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: (theme) => theme.resqPalette.border.focused,
            borderWidth: 2,
        },
        '&.Mui-disabled': {
            backgroundColor: (theme) => theme.resqPalette.surface.disabled,
            '& .MuiOutlinedInput-notchedOutline': {
                borderColor: (theme) => theme.resqPalette.border.subdued,
            },
            '& .MuiOutlinedInput-input, & .MuiInputBase-input': {
                backgroundColor: 'transparent',
                color: (theme) =>
                    theme.palette.mode === 'dark'
                        ? theme.resqPalette.text.subdued
                        : theme.resqPalette.text.disabled,
                WebkitTextFillColor: (theme) =>
                    theme.palette.mode === 'dark'
                        ? theme.resqPalette.text.subdued
                        : theme.resqPalette.text.disabled,
            },
        },
    };
    const resolvedOutlinedSx: SxProps<Theme> = inputSx
        ? ([baseOutlinedSx, inputSx] as SxProps<Theme>)
        : baseOutlinedSx;

    return (
        <Field
            editable={editable}
            error={error}
            label={label}
            labelAdornment={labelAdornment}
            inlineLabelAdornment={inlineLabelAdornment}
            tooltip={tooltip}
            helperText={helperText}
            helperTextStyle={helperTextStyle}
            wide={wide}
            required={required}
            errorId={errorId}
            elementType={ClickElementType.TEXT_INPUT}
            name={inputProps.name}
        >
            {!editable ? (
                isMarkdownEditor && stringValue ? (
                    <React.Suspense fallback={<Typography role="body">{stringValue}</Typography>}>
                        <MarkdownInput value={stringValue} editable={false} />
                    </React.Suspense>
                ) : (
                    <Typography role="body" sx={{ overflowWrap: 'break-word' }}>
                        {value ? String(value) : emptyNode}
                    </Typography>
                )
            ) : isMarkdownEditor ? (
                <React.Suspense
                    fallback={
                        <Skeleton
                            variant="rounded"
                            height={inputProps.rows ? Number(inputProps.rows) * 48 : 200}
                        />
                    }
                >
                    <MarkdownInput
                        value={stringValue}
                        onChange={onChange}
                        trimWhiteSpace={trimWhiteSpace}
                        autoCapitalize={autoCapitalize}
                        autoCorrect={autoCorrect}
                        spellCheck={spellCheck}
                        inputProps={inputElementProps}
                        {...inputProps}
                    />
                </React.Suspense>
            ) : variant === 'text' ? (
                <Input
                    error={!!error}
                    fullWidth
                    disableUnderline={disableUnderline}
                    inputProps={{
                        ...inputElementProps,
                        autoCapitalize,
                        autoCorrect,
                        spellCheck,
                        onBlur: (event) => {
                            if (trimWhiteSpace && onChange) {
                                event.target.value = event.target.value.trim();
                                onChange(event);
                            }
                            inputElementProps?.onBlur?.(event);
                        },
                    }}
                    value={value}
                    onChange={onChange}
                    {...inputProps}
                />
            ) : (
                <OutlinedInput
                    sx={resolvedOutlinedSx}
                    error={!!error}
                    fullWidth
                    inputProps={{
                        ...inputElementProps,
                        autoCapitalize,
                        autoCorrect,
                        spellCheck,
                        onBlur: (event) => {
                            if (trimWhiteSpace && onChange) {
                                event.target.value = event.target.value.trim();
                                onChange(event);
                            }
                            inputElementProps?.onBlur?.(event);
                        },
                    }}
                    value={value}
                    onChange={onChange}
                    {...inputProps}
                />
            )}
        </Field>
    );
};
export const TextField = withFormConsumer<NonNullable<OutlinedInputProps['value']>, TextFieldProps>(
    Text,
);
